import { useMemo } from 'react'
import { Dimension } from '../../../../../features/dimension/types/Dimension'
import { getDimensionTree } from '../../../../../features/dimension/components/utils'

export interface MoveTargetTreeNode extends Dimension {
  disabled?: boolean
  children?: MoveTargetTreeNode[]
}

export interface LegalMoveTargets {
  illegalIds: Set<string>
  legalIds: string[]
  treeData: MoveTargetTreeNode[]
}

/**
 * `excludeIds` plus every one of their descendants (a subtree can't move into itself). Extracted
 * so non-hook call sites (e.g. cellEditorParams factories) can reuse the same walk as
 * `useLegalMoveTargets` without going through a hook.
 */
export const computeIllegalReparentIds = (flatList: Dimension[], excludeIds: string[]): Set<string> => {
  const childrenByParent = new Map<string, string[]>()
  flatList.forEach(d => {
    if (d.parentDimensionId) {
      if (!childrenByParent.has(d.parentDimensionId)) childrenByParent.set(d.parentDimensionId, [])
      childrenByParent.get(d.parentDimensionId)!.push(d.dimensionId)
    }
  })

  const illegalIds = new Set<string>(excludeIds)
  const stack = [...excludeIds]
  while (stack.length) {
    const current = stack.pop()!
    const children = childrenByParent.get(current) || []
    children.forEach(childId => {
      if (!illegalIds.has(childId)) {
        illegalIds.add(childId)
        stack.push(childId)
      }
    })
  }

  return illegalIds
}

/**
 * Legal reparent targets for `excludeIds` (a single row, or a mass selection): every dimension
 * except the excluded ids themselves and all of their descendants (a subtree can't move into
 * itself). Shared by the inspector's parent field, the "Move under…" modal (single or mass), and
 * the list view's editable parent cell.
 */
export const useLegalMoveTargets = (flatList: Dimension[], excludeIds: string[]): LegalMoveTargets =>
  useMemo(() => {
    const illegalIds = computeIllegalReparentIds(flatList, excludeIds)
    const legalIds = flatList.map(d => d.dimensionId).filter(id => !illegalIds.has(id))

    const markDisabled = (nodes: Dimension[]): MoveTargetTreeNode[] =>
      nodes.map(node => ({
        ...node,
        disabled: illegalIds.has(node.dimensionId),
        children: node.children ? markDisabled(node.children) : undefined
      }))

    return { illegalIds, legalIds, treeData: markDisabled(getDimensionTree(flatList)) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flatList, excludeIds.join(',')])
